import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  toneChip
} from '../../component/common/designSystem';
import { habitStatusChipClass } from './habitsTheme';
import { fetchHabitsUserDetail, lastNDaysLabels } from './habitsHubApi';

/**
 * Habit Tracker → User detail.
 * One user's habit profile: tracking stats, their habits with streaks and
 * status, and a completions-per-day chart. Back destination is derived from
 * the B2B/B2C flow query params (companyId present = arrived via a company).
 *
 * Reused by the company panel via the `fetcher` / `backPath` / `backLabel`
 * props — a company admin cannot reach the admin endpoint, so the data source
 * has to be injectable rather than hardcoded.
 */
const HabitUserDetail = ({
  fetcher = fetchHabitsUserDetail,
  backPath: backPathProp,
  backLabel: backLabelProp
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  // Present only when arriving from a company page (B2B flow).
  const companyId = searchParams.get('companyId');

  const backPath =
    backPathProp ||
    (companyId ? `/habit-tracker/b2b/company/${companyId}` : '/habit-tracker/b2c');
  const backLabel = backLabelProp || (companyId ? 'Back to Company' : 'Back to B2C Logs');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const habits = detail?.habits || [];
  const completionsPerDay = detail?.completionsPerDay || [];

  const completionsOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: lastNDaysLabels(completionsPerDay.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => `${Math.round(val)}` } },
    tooltip: { y: { formatter: (val) => `${val} completions` } }
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>User Habit Detail | Shoorah Admin</title>
      </Helmet>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title="Active Habits"
          value={user ? user.activeHabits : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Total Completions"
          value={user ? user.totalCompletions : '-'}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Best Streak"
          value={user ? `${user.bestStreak} days` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.custom}
          title="Custom Habits"
          value={user ? user.customHabits : '-'}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 mt-6">
        <Panel title="Completions Per Day" subtitle="Last 30 days">
          {completionsPerDay.some((value) => value > 0) ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[{ name: 'Completions', data: completionsPerDay }]}
              options={completionsOptions}
            />
          ) : (
            <PanelEmpty>No completions recorded yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Habits" subtitle="Everything this user tracks or has tracked">
          {habits.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Habit</th>
                  <th className="py-2 pr-4 font-medium">Source</th>
                  <th className="py-2 pr-4 font-medium">Frequency</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium text-right">Current Streak</th>
                  <th className="py-2 pr-4 font-medium text-right">Longest Streak</th>
                  <th className="py-2 pr-4 font-medium text-right">Completions</th>
                  <th className="py-2 font-medium">Last Completed</th>
                </>
              }
            >
              {habits.map((habit) => (
                <tr key={habit.id} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{habit.name}</td>
                  <td className="py-2 pr-4">{habit.source === 'user' ? 'Custom' : 'Shoorah'}</td>
                  <td className="py-2 pr-4">{habit.frequency}</td>
                  <td className="py-2 pr-4">
                    <Chip className={`capitalize ${habitStatusChipClass(habit.status)}`}>
                      {habit.status}
                    </Chip>
                  </td>
                  <td className="py-2 pr-4 text-right">{habit.currentStreak} days</td>
                  <td className="py-2 pr-4 text-right">{habit.longestStreak} days</td>
                  <td className="py-2 pr-4 text-right">{habit.totalCompletions}</td>
                  <td className="py-2">{habit.lastCompletedAt || '-'}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>This user is not tracking any habits yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

HabitUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string,
  backLabel: PropTypes.string
};

export default HabitUserDetail;
